import React from 'react';
import { makeStyles } from '@material-ui/styles';
import { Box, Container, Divider, Typography, Button } from '@material-ui/core';
import { Helmet } from 'react-helmet';
import { Link as RouterLink } from 'react-router-dom';
import usePageTitle from '../../hooks/usePageTitle';
import { APP_PATH_JOBS, APP_PATH_SIGNUP } from '../../config';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(12),
    background: theme.palette.background.default
  },
  introSection: {
    textAlign: 'center',
    marginBottom: theme.spacing(10)
  },
  pageTitle: {
    fontWeight: 700,
    fontSize: '3rem',
    lineHeight: 1.2,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem'
    }
  },
  pageSubtitle: {
    fontSize: '1.25rem',
    color: theme.palette.text.secondary,
    maxWidth: '700px',
    margin: '0 auto'
  },
  faqSection: {
    marginBottom: theme.spacing(6),
    maxWidth: '900px',
    margin: '0 auto',
    paddingBottom: theme.spacing(6)
  },
  question: {
    fontWeight: 600,
    fontSize: '1.5rem',
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.25rem'
    }
  },
  answer: {
    fontSize: '1.125rem',
    lineHeight: 1.8,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2)
  },
  faqDivider: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(6)
  },
  ctaSection: {
    textAlign: 'center',
    marginTop: theme.spacing(10),
    paddingTop: theme.spacing(8),
    borderTop: `1px solid ${theme.palette.divider}`
  },
  ctaText: {
    fontSize: '1.125rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3)
  },
  ctaButtons: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'center',
    flexWrap: 'wrap'
  },
  primaryBtn: {
    padding: theme.spacing(1.5, 4),
    fontSize: '1rem',
    fontWeight: 600,
    textTransform: 'none'
  },
  secondaryBtn: {
    padding: theme.spacing(1.5, 4),
    fontSize: '1rem',
    fontWeight: 600,
    textTransform: 'none',
    borderWidth: '2px',
    '&:hover': {
      borderWidth: '2px'
    }
  }
}));

const CandidateFAQ = () => {
  const classes = useStyles();

  usePageTitle('Candidate FAQ - Remote Job');

  return (
    <div className={classes.root}>
      <Helmet>
        <title>Candidate FAQ - Remote Job Platform | Remote Job</title>
        <meta
          name="description"
          content="Everything you need to know about finding remote jobs, applying, and managing applications on Remote Job. Free for job seekers."
        />
        <meta
          name="keywords"
          content="remote job search, how to apply for remote jobs, job seeker FAQ, remote work questions, free job board"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Candidate FAQ - Remote Job Platform | Remote Job" />
        <meta
          property="og:description"
          content="Everything you need to know about finding remote jobs, applying, and managing applications on Remote Job."
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Candidate FAQ - Remote Job Platform | Remote Job" />
        <meta
          name="twitter:description"
          content="Everything you need to know about finding remote jobs, applying, and managing applications on Remote Job."
        />
      </Helmet>

      <Container maxWidth="lg">
        {/* Intro Section */}
        <Box className={classes.introSection}>
          <Typography className={classes.pageTitle} variant="h1">
            Finding jobs on Remote Job
          </Typography>
          <Typography className={classes.pageSubtitle} variant="h5">
            Everything you need to know about discovering opportunities, applying for jobs, and managing your applications on Remote Job.
          </Typography>
        </Box>

        {/* FAQ Items */}
        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            Is Remote Job free for job seekers?
          </Typography>
          <Typography className={classes.answer}>
            Yes. Remote Job is completely free for candidates. You can browse jobs, apply, message companies, and manage your applications at no cost.
          </Typography>
        </Box>

        <Divider className={classes.faqDivider} />

        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            Do I need an account to apply for jobs?
          </Typography>
          <Typography className={classes.answer}>
            You can browse jobs without an account. To apply and manage your applications, you'll need to create a free candidate profile.
          </Typography>
        </Box>

        <Divider className={classes.faqDivider} />

        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            How do I apply for a job?
          </Typography>
          <Typography className={classes.answer}>
            Open a job listing and click Apply. Your application is sent directly to the hiring company and appears in their hiring workspace immediately.
          </Typography>
        </Box>

        <Divider className={classes.faqDivider} />

        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            Can I apply to multiple jobs?
          </Typography>
          <Typography className={classes.answer}>
            Yes. You can apply to as many jobs as you like. Each application is tracked separately so you can follow your progress.
          </Typography>
        </Box>

        <Divider className={classes.faqDivider} />

        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            How can I track my applications?
          </Typography>
          <Typography className={classes.answer}>
            Once logged in, you can view all your applications in one place. You can see where each application stands and whether the company has taken action.
          </Typography>
        </Box>

        <Divider className={classes.faqDivider} />

        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            Can companies message me directly?
          </Typography>
          <Typography className={classes.answer}>
            Yes. Companies can message you directly through the platform. All conversations stay organized by job so you never lose track.
          </Typography>
        </Box>

        <Divider className={classes.faqDivider} />

        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            Will I receive interview invitations through Remote Job?
          </Typography>
          <Typography className={classes.answer}>
            Yes. If a company wants to interview you, they can send interview details directly through the platform. You'll receive notifications when interviews are scheduled.
          </Typography>
        </Box>

        <Divider className={classes.faqDivider} />

        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            Do companies see all my information?
          </Typography>
          <Typography className={classes.answer}>
            Companies only see the information you choose to include in your application and profile. Your data is not shared outside the hiring process.
          </Typography>
        </Box>

        <Divider className={classes.faqDivider} />

        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            Can I apply to remote jobs from anywhere?
          </Typography>
          <Typography className={classes.answer}>
            Yes. Remote Job focuses on remote-friendly roles. Some jobs may be restricted to certain regions depending on company requirements.
          </Typography>
        </Box>

        <Divider className={classes.faqDivider} />

        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            Why do some jobs mention location restrictions?
          </Typography>
          <Typography className={classes.answer}>
            Some companies hire only in specific countries or regions due to legal, time zone, or business requirements. These restrictions are clearly shown on the job listing.
          </Typography>
        </Box>

        <Divider className={classes.faqDivider} />

        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            How does Remote Job compare to traditional job boards?
          </Typography>
          <Typography className={classes.answer}>
            Remote Job connects you directly with hiring teams. Communication happens directly with the company, without recruiters in between.
          </Typography>
        </Box>

        <Divider className={classes.faqDivider} />

        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            Will my profile be visible to companies without applying?
          </Typography>
          <Typography className={classes.answer}>
            No. Companies only see your profile when you apply to a job. Your profile is not publicly searchable.
          </Typography>
        </Box>

        <Divider className={classes.faqDivider} />

        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            Is my data secure?
          </Typography>
          <Typography className={classes.answer}>
            Yes. Your data is handled securely and used only for the purpose of connecting you with hiring companies.
          </Typography>
        </Box>

        <Divider className={classes.faqDivider} />

        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            What happens after I apply?
          </Typography>
          <Typography className={classes.answer}>
            Your application goes straight to the company's hiring workspace. From there, they may review it, message you, or invite you to interview.
          </Typography>
        </Box>

        <Divider className={classes.faqDivider} />

        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            How do I get started?
          </Typography>
          <Typography className={classes.answer}>
            Create a free candidate profile, browse open roles, and apply to jobs that match your skills and interests.
          </Typography>
        </Box>

        <Divider />

        {/* CTA Section */}
        <Box className={classes.ctaSection}>
          <Typography className={classes.ctaText}>
            Ready to find your next opportunity? Browse jobs or create your free profile.
          </Typography>
          <Box className={classes.ctaButtons}>
            <Button
              component={RouterLink}
              to={APP_PATH_JOBS}
              variant="outlined"
              className={classes.secondaryBtn}>
              Browse Jobs
            </Button>
            <Button
              component={RouterLink}
              to={APP_PATH_SIGNUP}
              variant="contained"
              color="primary"
              className={classes.primaryBtn}>
              Create Profile
            </Button>
          </Box>
        </Box>
      </Container>
    </div>
  );
};

export default CandidateFAQ;
